// src/components/AnalyticsPanel.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  getDocs, 
  orderBy, 
  where, 
  limit, 
  getCountFromServer,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './AnalyticsPanel.css';

// --- CONFIGURATION ---
const EVENT_MAP = {
  "DR0001": "Saw a Doctor",
  "DR0002": "Place to Sleep",
  "DR0003": "Saw a Dentist",
  "DR0004": "Got Food",
  "DR0005": "Got New Clothes",
  "DR0006": "Counseling",
  "DR0007": "Support Group",
  "DR0008": "Get a Ride",
  "CT0001": "Built a Resume",
  "CT0002": "Made a PDF",
  "CT0003": "Resume Video",
  "CT0004": "Interview Video",
  "CT0005": "Appearance Video",
  "CT0006": "Job Search Video"
};

const BRAND_BLUE = '#2c3e50'; 

function AnalyticsPanel() {
  // --- EXISTING STATE ---
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // All Time Stats
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalStatesCount, setTotalStatesCount] = useState(0);

  // This Week Stats
  const [thisWeekUsers, setThisWeekUsers] = useState(0);
  const [thisWeekEvents, setThisWeekEvents] = useState(0);
  const [thisWeekStatesCount, setThisWeekStatesCount] = useState(0);

  // Chart Data
  const [eventBreakdown, setEventBreakdown] = useState([]);
  const [stateBreakdown, setStateBreakdown] = useState([]);

  // --- NEW STATE FOR REVIEWS & FEEDBACK ---
  const [totalReviewsCount, setTotalReviewsCount] = useState(0);
  const [fiveStarCount, setFiveStarCount] = useState(0);
  const [helpMessages, setHelpMessages] = useState([]);
  const [feedbackMessages, setFeedbackMessages] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  // --- Helper: Clean State Names ---
  const cleanStateName = (rawState) => {
    if (!rawState) return "Unknown";
    return rawState;
  };

  // --- Helper: Format Date ---
  const formatTimestamp = (ts) => {
    if (!ts) return 'No Date';
    // Handle Firestore Timestamp or standard Date string
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  const processLogs = useCallback((logs) => {
    // 1. Setup Counters
    const uniqueUsersAllTime = new Set();
    const uniqueStatesAllTime = new Set();
    let eventsCountAllTime = 0;

    const uniqueUsersThisWeek = new Set();
    const uniqueStatesThisWeek = new Set();
    let eventsCountThisWeek = 0;

    const eventCountsChart = {}; 
    const stateCountsChart = {}; 
    
    // Initialize chart keys
    Object.values(EVENT_MAP).forEach(name => eventCountsChart[name] = 0);

    // Get "7 Days Ago" in YYYY-MM-DD for a rolling week window
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    
    const year = sevenDaysAgo.getFullYear();
    const month = String(sevenDaysAgo.getMonth() + 1).padStart(2, '0');
    const day = String(sevenDaysAgo.getDate()).padStart(2, '0');
    const sevenDaysAgoStr = `${year}-${month}-${day}`;

    // 2. Loop through logs
    logs.forEach(log => {
      const isThisWeek = (log.date >= sevenDaysAgoStr);

      // --- Users ---
      if (log.userID) {
        uniqueUsersAllTime.add(log.userID);
        if (isThisWeek) uniqueUsersThisWeek.add(log.userID);
      }

      // --- States ---
      const rawState = log.region_state || "Unknown";
      const state = cleanStateName(rawState);

      uniqueStatesAllTime.add(state);
      if (isThisWeek) uniqueStatesThisWeek.add(state);

      stateCountsChart[state] = (stateCountsChart[state] || 0) + 1;

      // --- Events ---
      if (log.events && Array.isArray(log.events)) {
        log.events.forEach(code => {
          const name = EVENT_MAP[code] || code; 
          
          eventsCountAllTime++;
          eventCountsChart[name] = (eventCountsChart[name] || 0) + 1;

          if (isThisWeek) {
            eventsCountThisWeek++;
          }
        });
      }
    });

    // 3. Update State
    setTotalUsers(uniqueUsersAllTime.size);
    setTotalEvents(eventsCountAllTime);
    setTotalStatesCount(uniqueStatesAllTime.size);

    setThisWeekUsers(uniqueUsersThisWeek.size);
    setThisWeekEvents(eventsCountThisWeek);
    setThisWeekStatesCount(uniqueStatesThisWeek.size);

    // 4. Format Chart Data
    
    // Services Chart Formatting
    const chartData = Object.keys(eventCountsChart).map(name => ({
      name: name,
      count: eventCountsChart[name]
    })).filter(item => item.count > 0).sort((a, b) => b.count - a.count); 

    setEventBreakdown(chartData);

    // Users by State Chart Formatting (Top 10 + Other)
    const stateDataRaw = Object.keys(stateCountsChart).map(st => ({
      name: st,
      count: stateCountsChart[st]
    })).sort((a, b) => b.count - a.count);

    let finalStateData = [];
    
    // Handle bundling into "Other" if there are more than 10 states
    if (stateDataRaw.length > 10) {
      const top9 = stateDataRaw.slice(0, 9);
      const others = stateDataRaw.slice(9);
      const othersCount = others.reduce((sum, stateObj) => sum + stateObj.count, 0);
      
      top9.push({ name: 'Other', count: othersCount });
      finalStateData = top9;
    } else {
      finalStateData = stateDataRaw;
    }

    // Calculate percentages for the labels
    const totalStateLogs = finalStateData.reduce((sum, item) => sum + item.count, 0);
    const stateDataWithPercentages = finalStateData.map(item => ({
      ...item,
      percentage: totalStateLogs > 0 ? Math.round((item.count / totalStateLogs) * 100) : 0
    }));

    setStateBreakdown(stateDataWithPercentages);

  }, []);

  // --- FETCH ANALYTICS DATA ---
  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "Analytics"), orderBy("date", "desc"));
      const snapshot = await getDocs(q);
      
      const logs = snapshot.docs.map(doc => doc.data());
      setRawData(logs);
      processLogs(logs);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError("Failed to load analytics data.");
      setLoading(false);
    }
  }, [processLogs]);

  // --- FETCH REVIEWS & FEEDBACK DATA ---
  const fetchReviewsData = useCallback(async () => {
    setLoadingReviews(true);
    try {
      // 1. Get Counts (Efficiently)
      const reviewsColl = collection(db, "Reviews");
      
      // Total Reviews Count
      const snapshotTotal = await getCountFromServer(reviewsColl);
      setTotalReviewsCount(snapshotTotal.data().count);

      // 5-Star Reviews Count
      const qFiveStar = query(reviewsColl, where("rating", "==", 5));
      const snapshotFiveStar = await getCountFromServer(qFiveStar);
      setFiveStarCount(snapshotFiveStar.data().count);

      // 2. Get User Needs Help Inbox (Limit 50)
      const helpQuery = query(collection(db, "User_Needs_Help"), orderBy("timestamp", "desc"), limit(50));
      const helpSnapshot = await getDocs(helpQuery);
      setHelpMessages(helpSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // 3. Get Low Star Feedback Inbox (Limit 50)
      const feedbackQuery = query(collection(db, "Low_Star_Feedback"), orderBy("timestamp", "desc"), limit(50));
      const feedbackSnapshot = await getDocs(feedbackQuery);
      setFeedbackMessages(feedbackSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    } catch (err) {
      console.error("Error fetching reviews data:", err);
      // We don't block the whole page if this fails, just log it
    } finally {
      setLoadingReviews(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalyticsData();
    fetchReviewsData();
  }, [fetchAnalyticsData, fetchReviewsData]);

  // --- HANDLE STATUS UPDATES ---
  const updateMessageStatus = async (collectionName, docId, status) => {
    try {
      const docRef = doc(db, collectionName, docId);
      await updateDoc(docRef, { adminStatus: status });

      // Optimistically update UI
      if (collectionName === "User_Needs_Help") {
        setHelpMessages(prev => prev.map(msg => msg.id === docId ? { ...msg, adminStatus: status } : msg));
      } else if (collectionName === "Low_Star_Feedback") {
        setFeedbackMessages(prev => prev.map(msg => msg.id === docId ? { ...msg, adminStatus: status } : msg));
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status. Check console.");
    }
  };

  // --- HANDLE DELETE ---
  const deleteMessage = async (collectionName, docId) => {
    const isConfirmed = window.confirm("Are you sure you want to permanently delete this message?");
    if (!isConfirmed) return;

    try {
      const docRef = doc(db, collectionName, docId);
      await deleteDoc(docRef);

      // Remove from UI immediately
      if (collectionName === "User_Needs_Help") {
        setHelpMessages(prev => prev.filter(msg => msg.id !== docId));
      } else if (collectionName === "Low_Star_Feedback") {
        setFeedbackMessages(prev => prev.filter(msg => msg.id !== docId));
      }
    } catch (err) {
      console.error("Error deleting document:", err);
      alert("Failed to delete the message. You may need to update Security Rules if you haven't yet.");
    }
  };

  const downloadCSV = () => {
    if (rawData.length === 0) return;

    const allStates = new Set();
    rawData.forEach(log => {
        const s = cleanStateName(log.region_state);
        allStates.add(s);
    });
    const stateList = Array.from(allStates).sort();

    const rows = [];
    const header = ["Service Name", "Total Requests", ...stateList];
    rows.push(header.join(","));

    Object.entries(EVENT_MAP).forEach(([code, name]) => {
      let rowTotal = 0;
      let rowStateCounts = {};
      stateList.forEach(s => rowStateCounts[s] = 0);

      rawData.forEach(log => {
        if (log.events && log.events.includes(code)) {
          const countInLog = log.events.filter(e => e === code).length;
          rowTotal += countInLog;
          
          const st = cleanStateName(log.region_state);
          if (rowStateCounts[st] !== undefined) {
            rowStateCounts[st] += countInLog;
          }
        }
      });

      const line = [
        `"${name}"`,
        rowTotal,
        ...stateList.map(st => rowStateCounts[st])
      ];
      rows.push(line.join(","));
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Refuge_Analytics_Report_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- RENDER MESSAGE ITEM ---
  const renderMessageItem = (msg, collectionName) => {
    const isRead = msg.adminStatus === 'read';
    const isHelped = msg.adminStatus === 'helped';
    
    // Determine background style based on status
    let statusStyle = {};
    if (isHelped) statusStyle = { borderLeft: '4px solid #28a745', backgroundColor: '#f0fff4' };
    else if (isRead) statusStyle = { borderLeft: '4px solid #718096', backgroundColor: '#f7fafc', opacity: 0.8 };
    else statusStyle = { borderLeft: '4px solid #dd6b20' }; // Unread (orange)

    return (
      <li key={msg.id} className="inbox-item" style={statusStyle}>
        <div className="inbox-item-header">
          <span>{msg.userEmail || "Anonymous"}</span>
          <span>{formatTimestamp(msg.timestamp)}</span>
        </div>
        <div className="inbox-item-body">
          {/* Try to display message content, fallback to specific fields or generic text */}
          {msg.message || msg.feedback || msg.note || "(No text content provided)"}
          {msg.rating && <div><strong>Rating:</strong> {msg.rating} Stars</div>}
        </div>
        <div className="inbox-actions">
          {!isHelped && (
            <button 
              className="btn-action btn-mark-helped"
              onClick={() => updateMessageStatus(collectionName, msg.id, 'helped')}
            >
              Mark Helped
            </button>
          )}
          {!isRead && !isHelped && (
            <button 
              className="btn-action btn-mark-read"
              onClick={() => updateMessageStatus(collectionName, msg.id, 'read')}
            >
              Mark Read
            </button>
          )}
          {(isRead || isHelped) && <span style={{fontSize: '0.8rem', color: '#718096', alignSelf:'center'}}>Status: {msg.adminStatus}</span>}
          
          <button 
            className="btn-action"
            style={{ marginLeft: 'auto', backgroundColor: '#e53e3e', color: 'white', border: 'none' }}
            onClick={() => deleteMessage(collectionName, msg.id)}
            title="Delete this message"
          >
            Delete
          </button>
        </div>
      </li>
    );
  };

  // Custom label formatter for the new State Bar Chart
  const renderStateBarLabel = (props) => {
    const { x, y, width, height, value, payload } = props;
    
    // Safety check: Prevents React crash if payload is empty during initial load
    if (!payload || typeof payload.percentage === 'undefined') {
        return null; 
    }

    return (
      <text x={x + width + 5} y={y + height / 2} fill="#666" dy=".33em" fontSize={12}>
        {payload.percentage}% ({value})
      </text>
    );
  };

  if (loading) return <div className="analytics-loading">Loading Analytics Data...</div>;
  if (error) return <div className="analytics-error">{error}</div>;

  return (
    <div className="analytics-container">
      
      <div className="analytics-header">
        <h4 className="report-title">APP USAGE REPORT</h4>
      </div>

      {/* Row 1: All Time Stats */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <h3>Total Users</h3>
          <p className="kpi-value">{totalUsers}</p>
          <span className="kpi-sub">Unique IDs (All Time)</span>
        </div>
        <div className="kpi-card">
          <h3>All Time Usage</h3>
          <p className="kpi-value">{totalEvents}</p>
          <span className="kpi-sub">Buttons Clicked</span>
        </div>
        <div className="kpi-card">
          <h3>All Time States</h3>
          <p className="kpi-value">{totalStatesCount}</p>
          <span className="kpi-sub">Regions with activity</span>
        </div>
      </div>

      {/* Row 2: This Week Stats */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <h3>This Week's Users</h3>
          <p className="kpi-value">{thisWeekUsers}</p>
          <span className="kpi-sub">Active This Week</span>
        </div>
        <div className="kpi-card">
          <h3>This Week's Usage</h3>
          <p className="kpi-value">{thisWeekEvents}</p>
          <span className="kpi-sub">Buttons Clicked This Week</span>
        </div>
        <div className="kpi-card">
          <h3>States This Week</h3>
          <p className="kpi-value">{thisWeekStatesCount}</p>
          <span className="kpi-sub">Regions Active This Week</span>
        </div>
      </div>

      <div className="charts-row">
        {/* Left Chart */}
        <div className="chart-wrapper">
          <h3>Services Requested</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={eventBreakdown} 
              layout="vertical" 
              margin={{ top: 5, right: 60, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} tickCount={3} />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={150} 
                tick={{fontSize: 12}} 
              />
              <Tooltip />
              <Bar 
                dataKey="count" 
                fill={BRAND_BLUE} 
                name="Clicks" 
                barSize={20} 
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Right Chart (UPDATED TO BAR CHART WITH ACTIONS) */}
        <div className="chart-wrapper">
          <h3>Activity by State</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={stateBreakdown} 
              layout="vertical" 
              margin={{ top: 5, right: 80, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} tickCount={3} />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={60} 
                tick={{fontSize: 12}} 
              />
              <Tooltip 
                formatter={(value, name, props) => [`${value} Actions (${props?.payload?.percentage || 0}%)`, 'Actions']}
              />
              <Bar 
                dataKey="count" 
                fill={BRAND_BLUE} 
                name="Actions" 
                barSize={20} 
                label={renderStateBarLabel}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="analytics-footer">
        <button className="btn btn-save" onClick={downloadCSV}>Export App Usage to CSV</button>
      </div>

      {/* --- REVIEWS & FEEDBACK (HIDDEN FOR NOW) --- */}
      {false && (
        <>
          <h4 className="reviews-section-title">Reviews & Feedback Report</h4>
          
          {/* Review KPIs */}
          <div className="kpi-grid">
             <div className="kpi-card">
              <h3>5 Star Reviews</h3>
              <p className="kpi-value">{loadingReviews ? "-" : fiveStarCount}</p>
              <span className="kpi-sub">Total 5 Star Ratings</span>
            </div>
            <div className="kpi-card">
              <h3>Total Reviews</h3>
              <p className="kpi-value">{loadingReviews ? "-" : totalReviewsCount}</p>
              <span className="kpi-sub">Total Ratings Received</span>
            </div>
          </div>

          {/* Message Inboxes */}
          <div className="inbox-grid">
            
            {/* Inbox 1: User Needs Help */}
            <div className="inbox-card">
              <div className="inbox-header">User Needs Help</div>
              <ul className="inbox-list">
                {loadingReviews ? (
                  <li className="inbox-item">Loading...</li>
                ) : helpMessages.length === 0 ? (
                  <li className="empty-inbox">No help requests found.</li>
                ) : (
                  helpMessages.map(msg => renderMessageItem(msg, "User_Needs_Help"))
                )}
              </ul>
            </div>

            {/* Inbox 2: Low Star Feedback */}
            <div className="inbox-card">
              <div className="inbox-header">Low Star Feedback</div>
              <ul className="inbox-list">
                 {loadingReviews ? (
                  <li className="inbox-item">Loading...</li>
                ) : feedbackMessages.length === 0 ? (
                  <li className="empty-inbox">No low star feedback found.</li>
                ) : (
                  feedbackMessages.map(msg => renderMessageItem(msg, "Low_Star_Feedback"))
                )}
              </ul>
            </div>

          </div>
        </>
      )}

    </div>
  );
}

export default AnalyticsPanel;